// src/features/business-functions/components/details/AccountDetailsTab.jsx
import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Skeleton,
} from '@mui/material';
import { accountToFormValues } from '../../adapters/accountFormAdapter';
import { ACCOUNT_STATUS_COPY } from '../../constants/accountConstants';
import { radiusPx } from '../../../intervene/styles/layoutCommon';

const EMPTY_VALUE = '—';

function displayValue(value) {
  const normalized = typeof value === 'string' ? value.trim() : value;
  if (normalized === null || normalized === undefined || normalized === '') {
    return EMPTY_VALUE;
  }
  return normalized;
}

function ReadOnlyField({
  label,
  value,
  helperText,
  multiline = false,
  rows = 1,
  sx,
}) {
  return (
    <TextField
      fullWidth
      label={label}
      value={displayValue(value)}
      multiline={multiline}
      rows={rows}
      disabled
      helperText={helperText}
      InputProps={{ readOnly: true, style: { borderRadius: 0 } }}
      sx={sx}
    />
  );
}

/**
 * Account Details Tab — read-only view of account configuration.
 */
export default function AccountDetailsTab({ account, isLoading, styles }) {
  const formData = useMemo(() => accountToFormValues(account), [account]);
  const readOnlyFieldSx = styles.detailsReadOnlyField;

  if (isLoading || !account) {
    return (
      <Box sx={styles.detailsTabRoot}>
        <Box sx={styles.detailsFormCard}>
          <Skeleton variant="text" width={220} height={32} />
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 0 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 0 }} />
            </Grid>
            <Grid item xs={12}>
              <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 0 }} />
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.detailsTabRoot}>
      <Box sx={styles.detailsFormCard}>
        <Box>
          <Typography sx={styles.detailsFormSectionTitle}>
            Account Details & Configuration
          </Typography>
          <Typography sx={styles.detailsFormSectionSubtitle}>
            View primary information, status, and account settings.
          </Typography>
        </Box>

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <ReadOnlyField
              label="Account Name"
              value={formData.name}
              sx={{
                ...readOnlyFieldSx,
                '& .MuiFormLabel-root.MuiInputLabel-root , .MuiInputLabel-root.Mui-disabled:not(.MuiInputLabel-shrink)': {
                  top: '-2px',
                },
                '& .MuiInputBase-root.MuiOutlinedInput-root': {
                  borderRadius: `${radiusPx.md} !important`,
                  overflow:'hidden'
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <ReadOnlyField
              label="Account Status"
              value={ACCOUNT_STATUS_COPY[formData.status] || formData.status}
              helperText={ACCOUNT_STATUS_COPY.statusHelper}
              sx={{
                ...readOnlyFieldSx,
                '& .MuiFormLabel-root.MuiInputLabel-root , .MuiInputLabel-root.Mui-disabled:not(.MuiInputLabel-shrink)': {
                  top: '-2px',
                },
                 '& .MuiInputBase-root.MuiOutlinedInput-root': {
                  borderRadius: `${radiusPx.md} !important`,
                  overflow:'hidden'
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <ReadOnlyField
              label="Account Description"
              value={formData.description}
              multiline
              rows={3}
              sx={{
                ...readOnlyFieldSx,
                '& .MuiFormLabel-root.MuiInputLabel-root , .MuiInputLabel-root.Mui-disabled:not(.MuiInputLabel-shrink)': {
                  top: '-2px',
                },
                 '& .MuiInputBase-root.MuiOutlinedInput-root': {
                  borderRadius: `${radiusPx.md} !important`,
                  overflow:'hidden'
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <ReadOnlyField
              label="Organisation Name"
              value={formData.organisationName}
              sx={{
                ...readOnlyFieldSx,
                '& .MuiFormLabel-root.MuiInputLabel-root , .MuiInputLabel-root.Mui-disabled:not(.MuiInputLabel-shrink)': {
                  top: '-2px',
                },
                 '& .MuiInputBase-root.MuiOutlinedInput-root': {
                  borderRadius: `${radiusPx.md} !important`,
                  overflow:'hidden'
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <ReadOnlyField
              label="Billing Email"
              value={formData.billingEmail}
              sx={{
                ...readOnlyFieldSx,
                '& .MuiFormLabel-root.MuiInputLabel-root , .MuiInputLabel-root.Mui-disabled:not(.MuiInputLabel-shrink)': {
                  top: '-2px',
                },
                 '& .MuiInputBase-root.MuiOutlinedInput-root': {
                  borderRadius: `${radiusPx.md} !important`,
                  overflow:'hidden'
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <ReadOnlyField
              label="Contact Phone"
              value={formData.contactPhone}
              sx={{
                ...readOnlyFieldSx,
                '& .MuiFormLabel-root.MuiInputLabel-root , .MuiInputLabel-root.Mui-disabled:not(.MuiInputLabel-shrink)': {
                  top: '-2px',
                },
                 '& .MuiInputBase-root.MuiOutlinedInput-root': {
                  borderRadius: `${radiusPx.md} !important`,
                  overflow:'hidden'
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <ReadOnlyField
              label="Address"
              value={formData.address}
              sx={{
                ...readOnlyFieldSx,
                '& .MuiFormLabel-root.MuiInputLabel-root , .MuiInputLabel-root.Mui-disabled:not(.MuiInputLabel-shrink)': {
                  top: '-2px',
                },
                 '& .MuiInputBase-root.MuiOutlinedInput-root': {
                  borderRadius: `${radiusPx.md} !important`,
                  overflow:'hidden'
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
            >
              Account Identifier & Metadata (Read-only)
            </Typography>
            <Box sx={{...styles.detailsReadOnlyBox ,  borderRadius: `${radiusPx.md} !important`,}}>
              ID: {account.id || EMPTY_VALUE} | Created: {account.createdAt || EMPTY_VALUE} | Last Updated: {account.updatedAt || EMPTY_VALUE}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
